import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";

import { API } from "../../config/api";

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [formData, setFormData] = useState({
    event_id: "",
    name: "",
    price_cents: 0,
    capacity: 100,
    description: "",
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    "Content-Type": "application/json"
  });

  const fetchData = async () => {
    try {
      const [ticketsRes, eventsRes] = await Promise.all([
        fetch(`${API}/admin/tickets`, { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }),
        fetch(`${API}/admin/events`, { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } })
      ]);

      if (ticketsRes.ok) setTickets(await ticketsRes.json());
      if (eventsRes.ok) setEvents(await eventsRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (ticket = null) => {
    if (ticket) {
      setEditingTicket(ticket);
      setFormData({
        event_id: ticket.event_id,
        name: ticket.name,
        price_cents: ticket.price_cents,
        capacity: ticket.capacity,
        description: ticket.description || "",
        is_active: ticket.is_active
      });
    } else {
      setEditingTicket(null);
      setFormData({
        event_id: events[0]?.id || "",
        name: "",
        price_cents: 0,
        capacity: 100,
        description: "",
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingTicket 
        ? `${API}/admin/tickets/${editingTicket.id}`
        : `${API}/admin/tickets`;
      
      const response = await fetch(url, {
        method: editingTicket ? "PUT" : "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingTicket ? "Ticket updated!" : "Ticket created!");
        setDialogOpen(false);
        fetchData();
      } else {
        toast.error("Failed to save ticket");
      }
    } catch (error) {
      toast.error("Error saving ticket");
    }
  };

  const handleDelete = async (ticketId) => {
    if (!confirm("Are you sure you want to delete this ticket type?")) return;

    try {
      const response = await fetch(`${API}/admin/tickets/${ticketId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
      });

      if (response.ok) {
        toast.success("Ticket deleted!");
        fetchData();
      } else {
        toast.error("Failed to delete ticket");
      }
    } catch (error) {
      toast.error("Error deleting ticket");
    }
  };

  const getEventName = (eventId) => {
    return events.find(e => e.id === eventId)?.title || "Unknown";
  };

  return (
    <div className="space-y-6" data-testid="admin-tickets">
      <div className="flex justify-between items-center">
        <h1 className="font-heading text-2xl text-kerala-green">Ticket Types</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-kerala-green hover:bg-kerala-green/90"
              onClick={() => handleOpenDialog()}
              disabled={events.length === 0}
              data-testid="add-ticket-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Ticket Type
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingTicket ? "Edit Ticket Type" : "Create Ticket Type"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="event_id">Event</Label>
                <Select
                  value={formData.event_id}
                  onValueChange={(value) => setFormData({ ...formData, event_id: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map(event => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1"
                  placeholder="e.g., Adult, Child, VIP"
                  required
                  data-testid="ticket-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Price ($)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={(formData.price_cents / 100).toFixed(2)}
                    onChange={(e) => setFormData({ ...formData, price_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="capacity">Capacity</Label>
                  <Input
                    id="capacity"
                    type="number"
                    min="1"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                    className="mt-1"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="mt-1"
                  placeholder="What's included..."
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-kerala-green hover:bg-kerala-green/90">
                  {editingTicket ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {events.length === 0 && !loading && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4 text-yellow-800">
          Please create an event first before adding ticket types.
        </div>
      )}

      {/* Tickets Table */}
      <div className="bg-white rounded-sm border border-kerala-gold/10">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No ticket types found.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Sold / Capacity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow key={ticket.id}>
                  <TableCell className="font-medium">{ticket.name}</TableCell>
                  <TableCell>{getEventName(ticket.event_id)}</TableCell>
                  <TableCell>${(ticket.price_cents / 100).toFixed(2)}</TableCell>
                  <TableCell>
                    {ticket.sold_count || 0} / {ticket.capacity}
                    <div className="w-24 h-2 bg-gray-200 rounded-full mt-1">
                      <div 
                        className="h-full bg-kerala-green rounded-full"
                        style={{ width: `${Math.min(100, ((ticket.sold_count || 0) / ticket.capacity) * 100)}%` }}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      ticket.is_active 
                        ? "bg-green-100 text-green-700" 
                        : "bg-gray-100 text-gray-700"
                    }`}>
                      {ticket.is_active ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(ticket)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(ticket.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
